import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Tooltip,
    Grid,
    CircularProgress,
    IconButton,
    Collapse,
    useTheme,
    useMediaQuery
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyIcon from '@mui/icons-material/Key';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import ComputerIcon from '@mui/icons-material/Computer';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import SpeedIcon from '@mui/icons-material/Speed';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import axios from 'axios';

const LockdownAppSettingsDialog = ({
    open,
    onClose,
    settings
}) => {
    // Responsive hooks
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    // Track screen orientation
    const [screenOrientation, setScreenOrientation] = useState(
        window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
    );
    
    // Controls panel visibility on mobile
    const [showControlsPanel, setShowControlsPanel] = useState(!isMobile);
    
    // Add state for confirmation dialogs
    const [showUnlockConfirmDialog, setShowUnlockConfirmDialog] = useState(false);
    const [showRebootConfirmDialog, setShowRebootConfirmDialog] = useState(false);
    const [showResetPINConfirmDialog, setShowResetPINConfirmDialog] = useState(false);
    const [showRemoteAccessConfirmDialog, setShowRemoteAccessConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showDeleteBugReportDialog, setShowDeleteBugReportDialog] = useState(false);
    const [showHelpDialog, setShowHelpDialog] = useState(false);
    const [bugReportAdded, setBugReportAdded] = useState(null);
    const [screenshots, setScreenshots] = useState([]);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [clickCoordinates, setClickCoordinates] = useState(null);
    
    // Drag gesture state
    const [dragStart, setDragStart] = useState(null);
    const [dragEnd, setDragEnd] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragThreshold = 20; // Minimum pixels to consider it a drag vs tap
    
    // Touch gesture tracking
    const screenContainerRef = useRef(null);
    
    // Keyboard input state
    const typedTextRef = useRef(''); // Track text we've typed (not what's in the field)
    const textSendTimeoutRef = useRef(null); // Debounce timer for sending text

    // Add local state for device status
    const [deviceStatus, setDeviceStatus] = useState({
        Unlock: settings.Unlock || false,
        Reboot: settings.Reboot || false,
        ResetDevicePIN: settings.ResetDevicePIN || false,
        RemoteAccessMode: settings.RemoteAccessMode || false
    });

    // Add state for KioskManagerVersionReported
    const [kioskManagerVersion, setKioskManagerVersion] = useState(settings.KioskManagerVersionReported);
    const [lastQueried, setLastQueried] = useState(settings.LockdownAppLastQueried);

    // Add state for lockdownPollIntervalSeconds
    const [lockdownPollIntervalSeconds, setLockdownPollIntervalSeconds] = useState(null);

    // WebRTC state for remote access
    const [webrtcStatus, setWebrtcStatus] = useState('disconnected');
    const [isWebRTCActive, setIsWebRTCActive] = useState(false);
    const [webrtcScreenshot, setWebrtcScreenshot] = useState(null);
    const wsRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const dataChannelRef = useRef(null);
    const isConnectingRef = useRef(false);

    // ICE servers for NAT traversal - fetched securely from server (includes TURN)
    // Ref to avoid re-triggering the WebRTC effect when servers are fetched
    const iceServersRef = useRef([
        { urls: 'stun:stun.l.google.com:19302' } // Fallback STUN-only until fetched
    ]);

    // Helper function to send command via data channel (WebRTC) or HTTP (legacy)
    const sendCommand = useCallback(async (type, data) => {
        // Try WebRTC data channel first if available
        if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
            console.log('[WebRTC] Sending command via data channel:', type, data);
            dataChannelRef.current.send(JSON.stringify({ type, ...data }));
            return;
        }
        
        // Fall back to HTTP for legacy devices or when WebRTC isn't connected
        console.log('[Legacy] Sending command via HTTP:', type, data);
        try {
            await axios.post(`/api/tenants/${settings.id}/remote-access-command`, {
                type,
                ...data
            });
        } catch (error) {
            console.error('Error sending remote access command:', error);
        }
    }, [settings.id]);

    // WebRTC connection effect - runs when remote access mode changes
    useEffect(() => {
        // Only run when RemoteAccessMode is enabled and dialog is open
        if (!deviceStatus.RemoteAccessMode || !open) {
            // Clean up when not in remote access mode
            if (wsRef.current) {
                console.log('[WebRTC] Disconnecting from signaling server');
                wsRef.current.close();
                wsRef.current = null;
            }
            if (dataChannelRef.current) {
                dataChannelRef.current.close();
                dataChannelRef.current = null;
            }
            if (peerConnectionRef.current) {
                console.log('[WebRTC] Closing peer connection');
                peerConnectionRef.current.close();
                peerConnectionRef.current = null;
            }
            isConnectingRef.current = false;
            setWebrtcStatus('disconnected');
            setIsWebRTCActive(false);
            // Clean up blob URL to prevent memory leak
            setWebrtcScreenshot(prev => {
                if (prev && prev.blobUrl) {
                    URL.revokeObjectURL(prev.blobUrl);
                }
                return null;
            });
            return;
        }

        // Prevent multiple simultaneous connection attempts
        if (isConnectingRef.current || wsRef.current) {
            console.log('[WebRTC] Already connecting or connected, skipping');
            return;
        }

        // Helper function to send WebSocket message
        const sendMessage = (message) => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(message));
            }
        };

        // Helper function to handle WebRTC offer
        const handleOffer = async (offer) => {
            console.log('[WebRTC] Creating peer connection to answer offer...');
            
            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
            }

            const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
            peerConnectionRef.current = pc;

            pc.onicecandidate = (event) => {
                if (event.candidate) {
                    console.log('[WebRTC] → Sending ICE candidate to device');
                    sendMessage({
                        type: 'webrtc-ice-candidate',
                        candidate: {
                            sdpMid: event.candidate.sdpMid,
                            sdpMLineIndex: event.candidate.sdpMLineIndex,
                            candidate: event.candidate.candidate
                        }
                    });
                }
            };

            pc.oniceconnectionstatechange = () => {
                console.log('[WebRTC] ICE connection state:', pc.iceConnectionState);
                if (pc.iceConnectionState === 'connected') {
                    console.log('[WebRTC] ✓✓✓ WEBRTC CONNECTED!');
                    setWebrtcStatus('webrtc-connected');
                } else if (pc.iceConnectionState === 'failed') {
                    console.log('[WebRTC] ✗ ICE connection failed');
                    setWebrtcStatus('webrtc-failed');
                } else if (pc.iceConnectionState === 'disconnected') {
                    console.log('[WebRTC] ✗ ICE connection disconnected');
                    setWebrtcStatus('webrtc-disconnected');
                }
            };

            pc.onconnectionstatechange = () => {
                console.log('[WebRTC] Connection state:', pc.connectionState);
            };

            // Handle incoming data channel from device (screenshots + command acks)
            pc.ondatachannel = (event) => {
                console.log('[WebRTC] ✓ Data channel received:', event.channel.label);
                const channel = event.channel;
                dataChannelRef.current = channel;
                
                // Enable binary transfer for optimized screenshots
                channel.binaryType = 'arraybuffer';
                
                channel.onopen = () => {
                    console.log('[WebRTC] ✓✓✓ DATA CHANNEL OPEN! Ready for screenshots and commands.');
                    setWebrtcStatus('webrtc-connected');
                    setIsWebRTCActive(true);
                };
                
                channel.onmessage = (e) => {
                    // Handle binary data (optimized screenshot format)
                    if (e.data instanceof ArrayBuffer) {
                        const bytes = new Uint8Array(e.data);
                        // Check header: 0x01 = binary WebP screenshot
                        if (bytes.length > 1 && bytes[0] === 0x01) {
                            // Extract WebP data (skip 1-byte header)
                            const webpData = bytes.slice(1);
                            // Create blob URL for efficient rendering
                            const blob = new Blob([webpData], { type: 'image/webp' });
                            const url = URL.createObjectURL(blob);
                            
                            setWebrtcScreenshot(prev => {
                                // Revoke previous URL to prevent memory leak
                                if (prev && prev.blobUrl) {
                                    URL.revokeObjectURL(prev.blobUrl);
                                }
                                return {
                                    blobUrl: url,
                                    timestamp: Date.now()
                                };
                            });
                        }
                        return;
                    }
                    
                    // Handle JSON messages (legacy format and control messages)
                    try {
                        const msg = JSON.parse(e.data);
                        if (msg.type === 'screenshot') {
                            // Legacy: Base64 encoded screenshot via JSON
                            setWebrtcScreenshot(prev => {
                                if (prev && prev.blobUrl) {
                                    URL.revokeObjectURL(prev.blobUrl);
                                }
                                return {
                                data: msg.data,
                                timestamp: msg.timestamp
                                };
                            });
                        } else if (msg.type === 'ready') {
                            console.log('[WebRTC] ✓ Device is ready:', msg.message);
                        } else if (msg.type === 'ack') {
                            console.log('[WebRTC] ✓ Command acknowledged:', msg.command);
                        } else {
                            console.log('[WebRTC] Data channel message:', msg.type);
                        }
                    } catch (err) {
                        console.log('[WebRTC] Data channel message (raw):', e.data);
                    }
                };
                
                channel.onclose = () => {
                    console.log('[WebRTC] Data channel closed');
                    dataChannelRef.current = null;
                    setIsWebRTCActive(false);
                };
                
                channel.onerror = (err) => {
                    console.error('[WebRTC] Data channel error:', err);
                };
            };

            await pc.setRemoteDescription(new RTCSessionDescription({
                type: offer.type,
                sdp: offer.sdp
            }));
            console.log('[WebRTC] ✓ Set remote description (offer)');

            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            console.log('[WebRTC] ✓ Created and set local description (answer)');

            sendMessage({
                type: 'webrtc-answer',
                answer: { type: answer.type, sdp: answer.sdp }
            });
            console.log('[WebRTC] → Sent answer to device');
            setWebrtcStatus('webrtc-connecting');
        };

        // Async function to fetch token and connect
        const connectWebSocket = async () => {
            isConnectingRef.current = true;
            setWebrtcStatus('connecting');

            // Fetch ICE servers (STUN/TURN) securely from server
            try {
                console.log('[WebRTC] Fetching ICE servers...');
                const iceResponse = await axios.get('/api/ice-servers');
                if (iceResponse.data.iceServers) {
                    iceServersRef.current = iceResponse.data.iceServers;
                    console.log('[WebRTC] Got ICE servers:', iceResponse.data.iceServers.length, 'servers');
                }
            } catch (err) {
                console.warn('[WebRTC] Failed to fetch ICE servers, using fallback STUN:', err.message);
                // Continue with fallback STUN - connection may still work on same network
            }

            // In development, we need to fetch the token via API
            const isDev = window.location.port === '3000';
            let tokenParam = '';
            
            if (isDev) {
                try {
                    console.log('[WebRTC] Fetching WebSocket token...');
                    const response = await axios.get('/api/ws-token');
                    tokenParam = `&bearerToken=${encodeURIComponent(response.data.token)}`;
                    console.log('[WebRTC] Got WebSocket token');
                } catch (err) {
                    console.error('[WebRTC] Failed to get WebSocket token:', err);
                    setWebrtcStatus('error-no-token');
                    isConnectingRef.current = false;
                    return;
                }
            }

            // Build WebSocket URL
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = isDev ? 'localhost:8000' : window.location.host;
            const wsUrl = `${protocol}//${host}/ws/remote-access?tenantId=${settings.id}&clientType=portal${tokenParam}`;
            console.log('[WebRTC] Connecting to WebSocket:', wsUrl.replace(/bearerToken=[^&]+/, 'bearerToken=TOKEN_HIDDEN'));

            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('[WebRTC] ✓ WebSocket connected');
                isConnectingRef.current = false;
                setWebrtcStatus('connected-signaling');
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    console.log('[WebRTC] Received message:', message.type);

                    switch (message.type) {
                        case 'connected':
                            console.log('[WebRTC] ✓ Received connection acknowledgment:', message);
                            if (message.hasDevice) {
                                console.log('[WebRTC] Device is already connected, requesting WebRTC connection');
                                sendMessage({ type: 'request-webrtc-connection' });
                            }
                            break;

                        case 'device-connected':
                            console.log('[WebRTC] ✓ Device connected! Requesting WebRTC connection');
                            sendMessage({ type: 'request-webrtc-connection' });
                            break;

                        case 'device-disconnected':
                            console.log('[WebRTC] ✗ Device disconnected');
                            if (peerConnectionRef.current) {
                                peerConnectionRef.current.close();
                                peerConnectionRef.current = null;
                            }
                            if (dataChannelRef.current) {
                                dataChannelRef.current = null;
                            }
                            setIsWebRTCActive(false);
                            setWebrtcScreenshot(null);
                            setWebrtcStatus('device-disconnected');
                            break;

                        case 'device-not-connected':
                            console.log('[WebRTC] Device not connected yet');
                            setWebrtcStatus('waiting-for-device');
                            break;

                        case 'webrtc-offer':
                            console.log('[WebRTC] → Received offer from device');
                            handleOffer(message.offer).catch(e => {
                                console.error('[WebRTC] Error handling offer:', e);
                            });
                            break;

                        case 'webrtc-ice-candidate':
                            console.log('[WebRTC] → Received ICE candidate from device');
                            if (peerConnectionRef.current) {
                                peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(message.candidate))
                                    .then(() => console.log('[WebRTC] ✓ Added ICE candidate'))
                                    .catch(e => console.error('[WebRTC] Error adding ICE candidate:', e));
                            }
                            break;

                        case 'webrtc-connection-state':
                            console.log('[WebRTC] Device connection state:', message.state);
                            break;

                        default:
                            console.log('[WebRTC] Unknown message type:', message.type);
                    }
                } catch (e) {
                    console.error('[WebRTC] Error parsing message:', e);
                }
            };

            ws.onclose = (event) => {
                console.log('[WebRTC] ✗ WebSocket closed:', event.code, event.reason);
                wsRef.current = null;
                isConnectingRef.current = false;
                setWebrtcStatus('disconnected');
            };

            ws.onerror = (error) => {
                console.error('[WebRTC] ✗ WebSocket error:', error);
                setWebrtcStatus('error');
            };
        };

        // Call the async connect function
        connectWebSocket();

        // Cleanup on unmount
        return () => {
            console.log('[WebRTC] Cleaning up WebRTC connection');
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            if (dataChannelRef.current) {
                dataChannelRef.current.close();
                dataChannelRef.current = null;
            }
            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
                peerConnectionRef.current = null;
            }
            isConnectingRef.current = false;
        };
    // Only depend on these stable values (iceServersRef is a ref, doesn't need to be in deps)
    }, [deviceStatus.RemoteAccessMode, open, settings.id]);

    // Add polling effect for device status and bug report
    useEffect(() => {
        let pollInterval;

        const checkDeviceStatus = async () => {
            try {
                const response = await axios.get(`/api/tenants/${settings.id}/unlock-status`, {
                    params: clickCoordinates ? {
                        clickX: clickCoordinates.x,
                        clickY: clickCoordinates.y
                    } : {}
                });
                const { status, reboot, resetDevicePIN, kioskManagerVersionReported, lockdownAppLastQueried, remoteAccessMode, lockdownPollIntervalSeconds: pollInterval } = response.data;
                setKioskManagerVersion(kioskManagerVersionReported);
                setLastQueried(lockdownAppLastQueried);
                setLockdownPollIntervalSeconds(pollInterval);
                // Check if any pending action has completed
                if (deviceStatus.Reboot && !reboot) {
                    setDeviceStatus(prev => ({ ...prev, Reboot: false }));
                    setSuccessMessage('Device reboot request was acknowledged by the device');
                    setShowSuccessDialog(true);
                }
                if (deviceStatus.ResetDevicePIN && !resetDevicePIN) {
                    setDeviceStatus(prev => ({ ...prev, ResetDevicePIN: false }));
                    setSuccessMessage('Device PIN reset request was acknowledged by the device');
                    setShowSuccessDialog(true);
                }
                if (deviceStatus.Unlock && status === 'lock') {
                    setDeviceStatus(prev => ({ ...prev, Unlock: false }));
                    setSuccessMessage('Device has been locked');
                    setShowSuccessDialog(true);
                }
                if (deviceStatus.RemoteAccessMode && !remoteAccessMode) {
                    setDeviceStatus(prev => ({ ...prev, RemoteAccessMode: false }));
                    setSuccessMessage('Remote access mode has been disabled');
                    setShowSuccessDialog(true);
                }

                // Check for bug report
                const bugReportResponse = await axios.get(`/api/tenants/${settings.id}/bug-report-status`);
                setBugReportAdded(bugReportResponse.data.bugReportAdded);

                // Clear click coordinates after sending
                if (clickCoordinates) {
                    setClickCoordinates(null);
                }
            } catch (error) {
                console.error('Error checking device status:', error);
            }
        };

        // Start polling if dialog is open
        if (open) {
            checkDeviceStatus(); // Initial check
            pollInterval = setInterval(checkDeviceStatus, 2000); // Poll every 2 seconds
        }
        
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [open, deviceStatus, settings.id, clickCoordinates]);

    // Add polling effect for screenshots when in remote access mode (legacy fallback)
    // Only poll for screenshots if WebRTC is NOT active (legacy devices)
    useEffect(() => {
        let pollInterval;

        const fetchScreenshots = async () => {
            // Skip screenshot polling if WebRTC is active (screenshots come via data channel)
            if (isWebRTCActive) {
                return;
            }
            
            if (deviceStatus.RemoteAccessMode) {
                try {
                    const response = await axios.get(`/api/tenants/${settings.id}/remote-screenshots`);
                    // Only keep the most recent screenshot
                    if (response.data.length > 0) {
                        setScreenshots([response.data[0]]);
                    }
                } catch (error) {
                    console.error('Error fetching screenshots:', error);
                }
            }
        };

        if (open && deviceStatus.RemoteAccessMode && !isWebRTCActive) {
            fetchScreenshots(); // Initial fetch
            pollInterval = setInterval(fetchScreenshots, 2000); // Poll every 2 seconds (legacy)
        }

        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [open, deviceStatus.RemoteAccessMode, settings.id, isWebRTCActive]);

    // Add effect to handle dialog close and visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && deviceStatus.RemoteAccessMode) {
                handleCancelRemoteAccess();
            }
        };

        // Add visibility change listener
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup function
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // If remote access is active when component unmounts, disable it
            if (deviceStatus.RemoteAccessMode) {
                handleCancelRemoteAccess();
            }
        };
    }, [deviceStatus.RemoteAccessMode]);

    // Add effect to handle dialog close
    useEffect(() => {
        if (!open && deviceStatus.RemoteAccessMode) {
            handleCancelRemoteAccess();
        }
    }, [open]);

    // Add handler functions
    const handleRebootRequest = async () => {
        try {
            await axios.post('/api/tenants-request-reboot', { tenantId: settings.id });
            setShowRebootConfirmDialog(false);
            // Update local state to show pending status
            setDeviceStatus(prev => ({
                ...prev,
                Reboot: true
            }));
        } catch (error) {
            console.error('Error requesting reboot:', error);
            alert('Failed to request reboot. Please try again.');
        }
    };

    const handleCancelReboot = async () => {
        try {
            await axios.post('/api/tenants-cancel-reboot', { tenantId: settings.id });
            // Update local state to clear pending status
            setDeviceStatus(prev => ({
                ...prev,
                Reboot: false
            }));
        } catch (error) {
            console.error('Error cancelling reboot:', error);
            alert('Failed to cancel reboot. Please try again.');
        }
    };

    const handlePINResetRequest = async () => {
        try {
            await axios.post('/api/tenants-request-pin-reset', { tenantId: settings.id });
            setShowResetPINConfirmDialog(false);
            // Update local state to show pending status
            setDeviceStatus(prev => ({
                ...prev,
                ResetDevicePIN: true
            }));
        } catch (error) {
            console.error('Error requesting PIN reset:', error);
            alert('Failed to request PIN reset. Please try again.');
        }
    };

    const handleCancelPINReset = async () => {
        try {
            await axios.post('/api/tenants-cancel-pin-reset', { tenantId: settings.id });
            // Update local state to clear pending status
            setDeviceStatus(prev => ({
                ...prev,
                ResetDevicePIN: false
            }));
        } catch (error) {
            console.error('Error cancelling PIN reset:', error);
            alert('Failed to cancel PIN reset. Please try again.');
        }
    };

    const handleUnlockRequest = async () => {
        try {
            await axios.post('/api/tenants-request-unlock', { tenantId: settings.id });
            setShowUnlockConfirmDialog(false);
            // Update local state to show pending status
            setDeviceStatus(prev => ({
                ...prev,
                Unlock: true
            }));
        } catch (error) {
            console.error('Error requesting Unlock:', error);
            alert('Failed to request Unlock. Please try again.');
        }
    };

    const handleCancelUnlock = async () => {
        try {
            await axios.post('/api/tenants-cancel-unlock', { tenantId: settings.id });
            // Update local state to clear pending status
            setDeviceStatus(prev => ({
                ...prev,
                Unlock: false
            }));
        } catch (error) {
            console.error('Error cancelling Unlock:', error);
            alert('Failed to cancel Unlock. Please try again.');
        }
    };

    const handleDeleteBugReport = async () => {
        try {
            await axios.delete(`/api/tenants/${settings.id}/bug-report`);
            setBugReportAdded(null);
            setShowDeleteBugReportDialog(false);
        } catch (error) {
            console.error('Error deleting bug report:', error);
            alert('Failed to delete bug report. Please try again.');
        }
    };

    const handleRemoteAccessRequest = async () => {
        try {
            await axios.post('/api/tenants-request-remote-access', { tenantId: settings.id });
            setShowRemoteAccessConfirmDialog(false);
            // Update local state to show pending status
            setDeviceStatus(prev => ({
                ...prev,
                RemoteAccessMode: true
            }));
        } catch (error) {
            console.error('Error requesting remote access:', error);
            alert('Failed to request remote access. Please try again.');
        }
    };

    const handleCancelRemoteAccess = async () => {
        try {
            await axios.post('/api/tenants-cancel-remote-access', { tenantId: settings.id });
            // Update local state to clear pending status
            setDeviceStatus(prev => ({
                ...prev,
                RemoteAccessMode: false
            }));
        } catch (error) {
            console.error('Error cancelling remote access:', error);
            alert('Failed to cancel remote access. Please try again.');
        }
    };

    // Add image load handler
    const handleImageLoad = (event) => {
        const img = event.target;
        setImageDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight
        });
    };

    // Helper to convert screen coordinates to device coordinates
    const screenToDeviceCoords = useCallback((screenX, screenY, rect) => {
        const containerAspectRatio = rect.width / rect.height;
        const imageAspectRatio = imageDimensions.width / imageDimensions.height;
        
        let actualX, actualY;
        
        if (containerAspectRatio > imageAspectRatio) {
            // Image is height-constrained
            const scaledWidth = rect.height * imageAspectRatio;
            const leftOffset = (rect.width - scaledWidth) / 2;
            const scale = imageDimensions.width / scaledWidth;
            actualX = Math.round((screenX - leftOffset) * scale);
            actualY = Math.round(screenY * (imageDimensions.height / rect.height));
        } else {
            // Image is width-constrained
            const scaledHeight = rect.width / imageAspectRatio;
            const topOffset = (rect.height - scaledHeight) / 2;
            const scale = imageDimensions.height / scaledHeight;
            actualX = Math.round(screenX * (imageDimensions.width / rect.width));
            actualY = Math.round((screenY - topOffset) * scale);
        }
        
        // Ensure coordinates are within bounds
        actualX = Math.max(0, Math.min(actualX, imageDimensions.width));
        actualY = Math.max(0, Math.min(actualY, imageDimensions.height));
        
        // Scale up by 2x to match device's actual resolution
        return { x: actualX * 2, y: actualY * 2 };
    }, [imageDimensions.width, imageDimensions.height]);

    // Mouse down - start potential drag
    const handleMouseDown = (event) => {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        setDragStart({ x, y, rect });
        setDragEnd({ x, y });
        setIsDragging(true);
    };

    // Mouse move - update drag end position
    const handleMouseMove = (event) => {
        if (!isDragging || !dragStart) return;
        
        const rect = dragStart.rect;
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        setDragEnd({ x, y });
    };

    // Mouse up - complete gesture (tap or drag)
    const handleMouseUp = (event) => {
        if (!isDragging || !dragStart) {
            setIsDragging(false);
            return;
        }
        
        const rect = dragStart.rect;
        const endX = event.clientX - rect.left;
        const endY = event.clientY - rect.top;
        
        // Calculate distance
        const distance = Math.sqrt(
            Math.pow(endX - dragStart.x, 2) + Math.pow(endY - dragStart.y, 2)
        );
        
        if (distance < dragThreshold) {
            // It's a tap
            const deviceCoords = screenToDeviceCoords(dragStart.x, dragStart.y, rect);
            sendCommand('tap', { x: deviceCoords.x, y: deviceCoords.y });
            
            // Reset tracked text when tapping (likely selecting a new input field)
            typedTextRef.current = '';
            
            // Show tap feedback
            setClickCoordinates({
                x: dragStart.x / rect.width,
                y: dragStart.y / rect.height
            });
        } else {
            // It's a drag
            const startCoords = screenToDeviceCoords(dragStart.x, dragStart.y, rect);
            const endCoords = screenToDeviceCoords(endX, endY, rect);
            
            sendCommand('drag-coords', { 
                startX: startCoords.x, 
                startY: startCoords.y,
                endX: endCoords.x,
                endY: endCoords.y
            });
        }
        
        // Reset drag state
        setDragStart(null);
        setDragEnd(null);
        setIsDragging(false);
    };

    // Mouse leave - cancel drag if in progress
    const handleMouseLeave = () => {
        if (isDragging) {
            setDragStart(null);
            setDragEnd(null);
            setIsDragging(false);
        }
    };

    // Touch start - start potential drag (for mobile)
    const handleTouchStart = useCallback((event) => {
        if (event.touches.length !== 1) return; // Only handle single touch
        
        const touch = event.touches[0];
        const rect = event.currentTarget.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        setDragStart({ x, y, rect });
        setDragEnd({ x, y });
        setIsDragging(true);
    }, []);

    // Touch move - update drag end position
    const handleTouchMove = useCallback((event) => {
        if (!isDragging || !dragStart || event.touches.length !== 1) return;
        
        event.preventDefault(); // Prevent scrolling while dragging
        
        const touch = event.touches[0];
        const rect = dragStart.rect;
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        setDragEnd({ x, y });
    }, [isDragging, dragStart]);

    // Touch end - complete gesture (tap or drag)
    const handleTouchEnd = useCallback((event) => {
        if (!isDragging || !dragStart) {
            setIsDragging(false);
            return;
        }
        
        const rect = dragStart.rect;
        const endX = dragEnd?.x ?? dragStart.x;
        const endY = dragEnd?.y ?? dragStart.y;
        
        // Calculate distance
        const distance = Math.sqrt(
            Math.pow(endX - dragStart.x, 2) + Math.pow(endY - dragStart.y, 2)
        );
        
        if (distance < dragThreshold) {
            // It's a tap
            const deviceCoords = screenToDeviceCoords(dragStart.x, dragStart.y, rect);
            sendCommand('tap', { x: deviceCoords.x, y: deviceCoords.y });
            
            // Reset tracked text when tapping (likely selecting a new input field)
            typedTextRef.current = '';
            
            // Show tap feedback
            setClickCoordinates({
                x: dragStart.x / rect.width,
                y: dragStart.y / rect.height
            });
        } else {
            // It's a drag/swipe
            const startCoords = screenToDeviceCoords(dragStart.x, dragStart.y, rect);
            const endCoords = screenToDeviceCoords(endX, endY, rect);
            
            sendCommand('drag-coords', { 
                startX: startCoords.x, 
                startY: startCoords.y,
                endX: endCoords.x,
                endY: endCoords.y
            });
        }
        
        // Reset drag state
        setDragStart(null);
        setDragEnd(null);
        setIsDragging(false);
    }, [isDragging, dragStart, dragEnd, sendCommand, screenToDeviceCoords]);

    // Update swipe handler to use sendCommand
    const handleSwipe = async (direction) => {
        sendCommand('swipe', { direction });
    };

    // Update drag handler to use sendCommand
    const handleDrag = async (direction) => {
        sendCommand('drag', { direction });
    };

    // Send queued text with debouncing to handle fast typing
    const sendQueuedText = useCallback(() => {
        if (typedTextRef.current.length > 0) {
            // Send the full text we've tracked
            sendCommand('text-full', { text: typedTextRef.current });
        }
    }, [sendCommand]);

    // Keyboard input handler for remote typing
    const handleKeyDown = useCallback((event) => {
        if (!deviceStatus.RemoteAccessMode) return;
        
        // Prevent default for most keys to avoid browser shortcuts
        // But allow Ctrl+C, Ctrl+V, etc. for clipboard operations
        if (!event.ctrlKey && !event.metaKey) {
            event.preventDefault();
        }
        
        const key = event.key;
        
        // Handle special keys immediately (no queuing)
        if (key === 'Backspace') {
            // Update our tracked text
            typedTextRef.current = typedTextRef.current.slice(0, -1);
            // Clear any pending send and send immediately
            if (textSendTimeoutRef.current) {
                clearTimeout(textSendTimeoutRef.current);
            }
            sendQueuedText();
        } else if (key === 'Enter') {
            // Send any pending text first, then enter
            if (textSendTimeoutRef.current) {
                clearTimeout(textSendTimeoutRef.current);
                sendQueuedText();
            }
            sendCommand('key', { action: 'enter' });
            // Clear tracked text after enter (usually submits form)
            typedTextRef.current = '';
        } else if (key === 'Tab') {
            // Send pending text, then tab to next field
            if (textSendTimeoutRef.current) {
                clearTimeout(textSendTimeoutRef.current);
                sendQueuedText();
            }
            sendCommand('key', { action: 'tab' });
            // Clear tracked text when moving to new field
            typedTextRef.current = '';
        } else if (key === 'Escape') {
            sendCommand('key', { action: 'back' }); // ESC acts as Android back
            typedTextRef.current = '';
        } else if (key === 'ArrowUp') {
            sendCommand('key', { action: 'dpad-up' });
        } else if (key === 'ArrowDown') {
            sendCommand('key', { action: 'dpad-down' });
        } else if (key === 'ArrowLeft') {
            sendCommand('key', { action: 'dpad-left' });
        } else if (key === 'ArrowRight') {
            sendCommand('key', { action: 'dpad-right' });
        } else if (key.length === 1) {
            // Regular character - add to our tracked text
            typedTextRef.current += key;
            
            // Debounce: wait 50ms for more keystrokes before sending
            if (textSendTimeoutRef.current) {
                clearTimeout(textSendTimeoutRef.current);
            }
            textSendTimeoutRef.current = setTimeout(() => {
                sendQueuedText();
            }, 50);
        }
    }, [deviceStatus.RemoteAccessMode, sendCommand, sendQueuedText]);

    // Add keyboard listener when in remote access mode
    useEffect(() => {
        if (deviceStatus.RemoteAccessMode) {
            window.addEventListener('keydown', handleKeyDown);
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                // Clear any pending text send timeout
                if (textSendTimeoutRef.current) {
                    clearTimeout(textSendTimeoutRef.current);
                }
            };
        }
    }, [deviceStatus.RemoteAccessMode, handleKeyDown]);

    // Track screen orientation changes
    useEffect(() => {
        const handleOrientationChange = () => {
            const newOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
            setScreenOrientation(newOrientation);
        };

        window.addEventListener('resize', handleOrientationChange);
        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            window.removeEventListener('resize', handleOrientationChange);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    // Determine connection mode for display
    const connectionMode = isWebRTCActive ? 'WebRTC (Fast ~5fps)' : 
                          (deviceStatus.RemoteAccessMode ? 'Legacy (~0.5fps)' : 'Disconnected');
    
    // Get the current screenshot to display (WebRTC binary, WebRTC legacy, or HTTP polling)
    const currentScreenshot = isWebRTCActive && webrtcScreenshot 
        ? { 
            // Prefer blobUrl (binary format) over data (Base64 format)
            url: webrtcScreenshot.blobUrl || `data:image/webp;base64,${webrtcScreenshot.data}`, 
            timestamp: webrtcScreenshot.timestamp 
          }
        : (screenshots.length > 0 ? screenshots[0] : null);

    // Determine if remote device orientation matches screen orientation
    const remoteDeviceOrientation = imageDimensions.width && imageDimensions.height
        ? (imageDimensions.width > imageDimensions.height ? 'landscape' : 'portrait')
        : 'portrait'; // Default to portrait
    const orientationsMatch = screenOrientation === remoteDeviceOrientation;
    
    // On mobile/tablet in remote access mode with matching orientation, go truly fullscreen
    const isFullscreenMode = isTablet && deviceStatus.RemoteAccessMode && orientationsMatch;
    
    // Determine if we should show the side panel (not on mobile fullscreen mode)
    const showSidePanel = !isFullscreenMode;

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth={false}
            fullWidth
            fullScreen={isFullscreenMode}
            PaperProps={{
                sx: isFullscreenMode ? {
                    // True fullscreen on mobile when orientations match
                    width: '100vw',
                    height: '100vh',
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                    m: 0,
                    borderRadius: 0
                } : deviceStatus.RemoteAccessMode ? {
                    // Large dialog for remote access on desktop/tablet
                    width: isMobile ? '100vw' : '98vw',
                    height: isMobile ? '100vh' : '98vh',
                    maxWidth: isMobile ? '100vw' : '98vw',
                    maxHeight: isMobile ? '100vh' : '98vh',
                    m: isMobile ? 0 : 1,
                    borderRadius: isMobile ? 0 : undefined
                } : {
                    // Normal dialog for settings
                    maxWidth: isMobile ? '100%' : '900px',
                    width: isMobile ? '100%' : undefined,
                    m: isMobile ? 0 : undefined,
                    borderRadius: isMobile ? 0 : undefined
                }
            }}
        >
            {/* Compact header when in remote access mode or fullscreen */}
            {!deviceStatus.RemoteAccessMode && !isFullscreenMode && (
                <DialogTitle sx={{ 
                    py: isMobile ? 1.5 : 2,
                    px: isMobile ? 2 : 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <span>Lockdown App Settings</span>
                    <IconButton 
                        onClick={() => setShowHelpDialog(true)}
                        size="small"
                        sx={{ ml: 1 }}
                    >
                        <HelpOutlineIcon />
                    </IconButton>
                </DialogTitle>
            )}
            <DialogContent sx={deviceStatus.RemoteAccessMode ? { 
                p: isFullscreenMode ? 0 : (isMobile ? 0.5 : 1), 
                overflow: 'hidden', 
                height: '100%' 
            } : {
                px: isMobile ? 2 : 3,
                py: isMobile ? 1 : 2
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    gap: isMobile ? 1 : 2,
                    flexDirection: deviceStatus.RemoteAccessMode 
                        ? (isFullscreenMode ? 'column' : (isMobile ? 'column' : 'row'))
                        : 'column',
                    height: deviceStatus.RemoteAccessMode ? '100%' : 'auto',
                    minHeight: deviceStatus.RemoteAccessMode ? 0 : 'auto'
                }}>
                    {/* Controls/Settings Panel - hidden in fullscreen mode, collapsible on mobile */}
                    {showSidePanel && (
                    <Box sx={{ 
                        flex: deviceStatus.RemoteAccessMode 
                            ? (isMobile ? '0 0 auto' : '0 0 280px') 
                            : '1 1 100%',
                        overflow: deviceStatus.RemoteAccessMode ? 'auto' : 'visible',
                        maxHeight: deviceStatus.RemoteAccessMode 
                            ? (isMobile ? (showControlsPanel ? '40vh' : 'auto') : '100%') 
                            : 'none',
                        pr: deviceStatus.RemoteAccessMode && !isMobile ? 1 : 0
                    }}>
                        {/* Collapsible controls header on mobile in remote access mode */}
                        {deviceStatus.RemoteAccessMode && isMobile && (
                            <Box 
                                onClick={() => setShowControlsPanel(!showControlsPanel)}
                                sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    backgroundColor: 'grey.100',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    mb: 1
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Device Controls
                            </Typography>
                                {showControlsPanel ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                        )}

                        <Collapse in={!deviceStatus.RemoteAccessMode || !isMobile || showControlsPanel}>
                        <Box sx={{ 
                            display: 'grid', 
                            // Responsive grid: 1 column on mobile, 2 on tablet+
                            gridTemplateColumns: deviceStatus.RemoteAccessMode 
                                ? '1fr' 
                                : (isMobile ? '1fr' : 'repeat(2, 1fr)'), 
                            gap: isMobile ? 1 : (deviceStatus.RemoteAccessMode ? 1 : 2), 
                            mt: deviceStatus.RemoteAccessMode ? 0 : (isMobile ? 1 : 2),
                            '& .MuiButton-root': {
                                minWidth: '100%',
                                height: deviceStatus.RemoteAccessMode ? '40px' : (isMobile ? '44px' : '48px'),
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: deviceStatus.RemoteAccessMode ? '0.8rem' : (isMobile ? '0.9rem' : '1rem')
                            }
                        }}>
                            <Tooltip title="Unlock the device. This will unlock the device.">
                                <Box>
                                    {deviceStatus.Unlock ? (
                                        <Button 
                                            variant="outlined" 
                                            color="error" 
                                            onClick={handleCancelUnlock}
                                            startIcon={<LockIcon />}
                                        >
                                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                            Cancel Unlock
                                        </Button>
                                    ) : (
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            onClick={() => setShowUnlockConfirmDialog(true)}
                                            startIcon={<LockOpenIcon />}
                                            sx={{ 
                                                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
                                            }}
                                        >
                                            Unlock Device
                                        </Button>
                                    )}
                                </Box>
                            </Tooltip>

                            <Tooltip title="Reboot the kiosk device. This will restart the device and all running applications.">
                                <Box>
                                    {deviceStatus.Reboot ? (
                                        <Button 
                                            variant="outlined" 
                                            color="error" 
                                            onClick={handleCancelReboot}
                                            startIcon={<RestartAltIcon />}
                                        >
                                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                            Cancel Reboot
                                        </Button>
                                    ) : (
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            onClick={() => setShowRebootConfirmDialog(true)}
                                            startIcon={<RestartAltIcon />}
                                            sx={{ 
                                                background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
                                                boxShadow: '0 3px 5px 2px rgba(255, 152, 0, .3)'
                                            }}
                                        >
                                            Request Reboot
                                        </Button>
                                    )}
                                </Box>
                            </Tooltip>

                            <Tooltip title="Reset the device PIN. This will reboot the device and require a new PIN to be set.">
                                <Box>
                                    {deviceStatus.ResetDevicePIN ? (
                                        <Button 
                                            variant="outlined" 
                                            color="error" 
                                            onClick={handleCancelPINReset}
                                            startIcon={<KeyIcon />}
                                        >
                                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                            Cancel Reset
                                        </Button>
                                    ) : (
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            onClick={() => setShowResetPINConfirmDialog(true)}
                                            startIcon={<KeyIcon />}
                                            sx={{ 
                                                background: 'linear-gradient(45deg, #4CAF50 30%, #81C784 90%)',
                                                boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)'
                                            }}
                                        >
                                            Reset PIN
                                        </Button>
                                    )}
                                </Box>
                            </Tooltip>

                            <Tooltip title="Enable remote access mode. This will allow you to remotely access the device via this portal.">
                                <Box>
                                    {deviceStatus.RemoteAccessMode ? (
                                        <Button 
                                            variant="outlined" 
                                            color="error" 
                                            onClick={handleCancelRemoteAccess}
                                            startIcon={<ComputerIcon />}
                                        >
                                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                            Cancel Remote Access
                                        </Button>
                                    ) : (
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            onClick={() => setShowRemoteAccessConfirmDialog(true)}
                                            startIcon={<ComputerIcon />}
                                            sx={{ 
                                                background: 'linear-gradient(45deg, #9C27B0 30%, #BA68C8 90%)',
                                                boxShadow: '0 3px 5px 2px rgba(156, 39, 176, .3)'
                                            }}
                                        >
                                            Enable Remote Access
                                        </Button>
                                    )}
                                </Box>
                            </Tooltip>
                        </Box>
                        </Collapse>

                        {bugReportAdded && !deviceStatus.RemoteAccessMode && (
                            <Box sx={{ 
                                mt: 3, 
                                p: 2, 
                                borderRadius: '8px',
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                border: '1px solid rgba(0, 0, 0, 0.12)',
                                position: 'relative'
                            }}>
                                <IconButton
                                    onClick={() => setShowDeleteBugReportDialog(true)}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: 8,
                                        color: 'text.secondary',
                                        '&:hover': {
                                            color: 'error.main'
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <BugReportIcon color="primary" />
                                    <Typography variant="subtitle1" color="primary">
                                        Bug Report Information
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Last bug report received: {new Date(bugReportAdded).toLocaleString()}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={async () => {
                                        try {
                                            const response = await axios.get(`/api/tenants/${settings.id}/bug-report`);
                                            if (response.data.downloadUrl) {
                                                console.log(response.data.downloadUrl);
                                                window.location.href = response.data.downloadUrl;
                                            } else {
                                                alert('Failed to get download URL');
                                            }
                                        } catch (error) {
                                            console.error('Error downloading bug report:', error);
                                            alert('Failed to download bug report');
                                        }
                                    }}
                                    startIcon={<BugReportIcon />}
                                    sx={{ 
                                        borderRadius: '8px',
                                        textTransform: 'none'
                                    }}
                                >
                                    Download Bug Report
                                </Button>
                            </Box>
                        )}

                        <Box sx={{ 
                            mt: deviceStatus.RemoteAccessMode ? 1 : (isMobile ? 2 : 3), 
                            p: deviceStatus.RemoteAccessMode ? 1 : (isMobile ? 1.5 : 2), 
                            borderRadius: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            border: '1px solid rgba(0, 0, 0, 0.12)'
                        }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: deviceStatus.RemoteAccessMode || isMobile ? '0.75rem' : undefined }}>
                                Device Information
                            </Typography>
                            <Grid container spacing={deviceStatus.RemoteAccessMode || isMobile ? 0.5 : 2}>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: deviceStatus.RemoteAccessMode || isMobile ? '0.75rem' : undefined }}>
                                            Last Checked In:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary" sx={{ fontSize: deviceStatus.RemoteAccessMode || isMobile ? '0.75rem' : undefined }}>
                                            {lastQueried ? new Date(lastQueried).toLocaleString() : 'Never'}
                                            {lastQueried && lockdownPollIntervalSeconds && (Date.now() - new Date(lastQueried).getTime() > lockdownPollIntervalSeconds * 1000 * 2) && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-block',
                                                        ml: 1,
                                                        px: 0.5,
                                                        py: 0.25,
                                                        borderRadius: '4px',
                                                        backgroundColor: 'error.main',
                                                        color: 'error.contrastText',
                                                        fontSize: '0.65rem',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    OFFLINE
                                                </Box>
                                            )}
                                        </Typography>
                                    </Box>
                                </Grid>
                                {kioskManagerVersion && (
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: deviceStatus.RemoteAccessMode || isMobile ? '0.75rem' : undefined }}>
                                                Version:
                                            </Typography>
                                            <Typography variant="body2" color="text.primary" sx={{ fontSize: deviceStatus.RemoteAccessMode || isMobile ? '0.75rem' : undefined }}>
                                                {kioskManagerVersion}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                {deviceStatus.RemoteAccessMode && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                    Mode:
                                                </Typography>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        px: 0.75,
                                                        py: 0.25,
                                                        borderRadius: '4px',
                                                        backgroundColor: isWebRTCActive ? 'success.main' : 'warning.main',
                                                        color: 'white',
                                                        fontSize: '0.65rem',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {isWebRTCActive && <SpeedIcon sx={{ fontSize: 12 }} />}
                                                    {connectionMode}
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                    Status:
                                                </Typography>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 0.75,
                                                        py: 0.25,
                                                        borderRadius: '4px',
                                                        backgroundColor: 
                                                            webrtcStatus === 'webrtc-connected' ? 'success.main' :
                                                            webrtcStatus.includes('connecting') ? 'warning.main' :
                                                            webrtcStatus.includes('error') || webrtcStatus.includes('failed') ? 'error.main' :
                                                            'grey.500',
                                                        color: 'white',
                                                        fontSize: '0.65rem',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {webrtcStatus.toUpperCase().replace(/-/g, ' ')}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>
                    </Box>
                    )}

                    {deviceStatus.RemoteAccessMode && (
                        <Box sx={{ 
                            flex: isFullscreenMode ? '1 1 100%' : '1 1 60%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderLeft: isFullscreenMode || isMobile ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
                            borderTop: isMobile && !isFullscreenMode ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                            pl: isFullscreenMode ? 0 : (isMobile ? 0 : 1),
                            pt: isMobile && !isFullscreenMode ? 1 : 0,
                            height: '100%',
                            minHeight: 0, // Allow flex child to shrink
                            position: 'relative'
                        }}>
                            {/* Floating close button */}
                            <IconButton
                                onClick={onClose}
                                sx={{
                                    position: 'absolute',
                                    top: isFullscreenMode ? 8 : 4,
                                    right: isFullscreenMode ? 8 : 4,
                                    zIndex: 10,
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.8)'
                                    },
                                    // Make touch target larger on mobile
                                    width: isMobile ? 44 : 32,
                                    height: isMobile ? 44 : 32
                                }}
                                size={isMobile ? "medium" : "small"}
                            >
                                <CloseIcon fontSize={isMobile ? "medium" : "small"} />
                            </IconButton>
                            
                            {/* Floating controls toggle for fullscreen mobile */}
                            {isFullscreenMode && (
                                <IconButton
                                    onClick={() => setShowControlsPanel(!showControlsPanel)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        left: 8,
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.8)'
                                        },
                                        width: 44,
                                        height: 44
                                    }}
                                    size="medium"
                                >
                                    <TouchAppIcon />
                                </IconButton>
                            )}
                            
                            {/* Floating swipe controls on fullscreen mobile */}
                            {isFullscreenMode && showControlsPanel && (
                            <Box sx={{ 
                                    position: 'absolute',
                                    top: 60,
                                    left: 8,
                                    zIndex: 10,
                                display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    borderRadius: 2,
                                    p: 1
                                }}>
                                    <IconButton 
                                        onClick={() => handleSwipe('up')}
                                        sx={{ color: 'white', width: 40, height: 40 }}
                                    >
                                        <HomeIcon />
                                    </IconButton>
                                    <IconButton 
                                        onClick={() => handleDrag('down')}
                                        sx={{ color: 'white', width: 40, height: 40 }}
                                    >
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        onClick={() => handleDrag('up')}
                                        sx={{ color: 'white', width: 40, height: 40 }}
                                    >
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            
                            <Box sx={{ 
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 1,
                                flex: 1,
                                minHeight: 0 // Allow flex child to shrink
                            }}>
                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    {/* WebRTC Video or Legacy Screenshot Display */}
                                    <Box 
                                        ref={screenContainerRef}
                                        sx={{ 
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                        }}
                                    >
                                        {/* Device Frame - respects actual device aspect ratio */}
                                        {/* Hide frame decoration in fullscreen mode for maximum screen usage */}
                                        <Box sx={{
                                            position: 'relative',
                                            // Use actual device aspect ratio, default to 9:16 portrait if unknown
                                            aspectRatio: imageDimensions.width && imageDimensions.height 
                                                ? `${imageDimensions.width} / ${imageDimensions.height}`
                                                : '9 / 16',
                                            height: '100%',
                                            maxWidth: '100%',
                                            backgroundColor: isFullscreenMode ? '#000' : '#1a1a1a',
                                            borderRadius: isFullscreenMode ? 0 : '24px',
                                            padding: isFullscreenMode ? 0 : '8px',
                                            boxShadow: isFullscreenMode ? 'none' : '0 20px 40px rgba(0,0,0,0.3), inset 0 0 0 2px rgba(255,255,255,0.1)'
                                        }}>
                                            {/* Camera notch - hide in fullscreen */}
                                            {!isFullscreenMode && (
                                            <Box sx={{
                                                position: 'absolute',
                                                top: '4px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '60px',
                                                height: '6px',
                                                backgroundColor: '#0a0a0a',
                                                borderRadius: '3px',
                                                zIndex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                {/* Camera lens */}
                                                <Box sx={{
                                                    width: '4px',
                                                    height: '4px',
                                                    backgroundColor: '#2a2a2a',
                                                    borderRadius: '50%'
                                                }} />
                                            </Box>
                                            )}
                                            
                                            {/* Screen */}
                                            <Box 
                                                sx={{ 
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: isFullscreenMode ? 0 : '16px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#000',
                                                    cursor: 'crosshair',
                                                    // Prevent text selection on touch
                                                    WebkitUserSelect: 'none',
                                                    userSelect: 'none',
                                                    // Prevent iOS Safari zoom on double-tap
                                                    touchAction: 'manipulation'
                                                }}>
                                                {/* Screenshot display (WebRTC or legacy) */}
                                                {currentScreenshot && (
                                                    <img 
                                                        src={currentScreenshot.url} 
                                                        alt="Remote Access Screenshot"
                                                        onLoad={handleImageLoad}
                                                        // Mouse events for desktop
                                                        onMouseDown={handleMouseDown}
                                                        onMouseMove={handleMouseMove}
                                                        onMouseUp={handleMouseUp}
                                                        onMouseLeave={handleMouseLeave}
                                                        // Touch events for mobile
                                                        onTouchStart={handleTouchStart}
                                                        onTouchMove={handleTouchMove}
                                                        onTouchEnd={handleTouchEnd}
                                                        draggable={false}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            userSelect: 'none',
                                                            WebkitUserSelect: 'none',
                                                            WebkitTouchCallout: 'none',
                                                            // Prevent iOS image save popup
                                                            pointerEvents: 'auto'
                                                        }}
                                                    />
                                                )}
                                                
                                                {/* Drag gesture indicator */}
                                                {isDragging && dragStart && dragEnd && (
                                                    <svg
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            pointerEvents: 'none',
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        {/* Drag line */}
                                                        <line
                                                            x1={dragStart.x}
                                                            y1={dragStart.y}
                                                            x2={dragEnd.x}
                                                            y2={dragEnd.y}
                                                            stroke="#00ff00"
                                                            strokeWidth="3"
                                                            strokeLinecap="round"
                                                            strokeDasharray="8,4"
                                                        />
                                                        {/* Start point */}
                                                        <circle
                                                            cx={dragStart.x}
                                                            cy={dragStart.y}
                                                            r="8"
                                                            fill="#00ff00"
                                                            fillOpacity="0.7"
                                                        />
                                                        {/* End point (arrow) */}
                                                        <circle
                                                            cx={dragEnd.x}
                                                            cy={dragEnd.y}
                                                            r="6"
                                                            fill="#00ff00"
                                                            stroke="#ffffff"
                                                            strokeWidth="2"
                                                        />
                                                    </svg>
                                                )}
                                                
                                                {/* Loading state */}
                                                {!currentScreenshot && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            color: 'white',
                                                            gap: 2
                                                        }}
                                                    >
                                                        <CircularProgress 
                                                            size={48} 
                                                            thickness={2}
                                                            sx={{ color: 'rgba(255, 255, 255, 0.5)' }} 
                                                        />
                                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                            Connecting to device...
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                            {webrtcStatus === 'webrtc-connected' ? 'Waiting for screenshots' : 'Establishing connection'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                
                                                {/* Stale screenshot overlay (only for legacy mode) */}
                                                {!isWebRTCActive && currentScreenshot && Date.now() - new Date(currentScreenshot.timestamp).getTime() > 10000 && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            color: 'white',
                                                            gap: 2,
                                                            animation: 'fadeIn 0.3s ease-in-out',
                                                            '@keyframes fadeIn': {
                                                                '0%': { opacity: 0 },
                                                                '100%': { opacity: 1 }
                                                            }
                                                        }}
                                                    >
                                                        <Typography 
                                                            variant="body1" 
                                                            sx={{ 
                                                                fontWeight: 'medium',
                                                                animation: 'pulse 2s ease-in-out infinite',
                                                                '@keyframes pulse': {
                                                                    '0%': { opacity: 0.6 },
                                                                    '50%': { opacity: 1 },
                                                                    '100%': { opacity: 0.6 }
                                                                },
                                                                px: 3,
                                                                py: 1.5,
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            Waiting for device response...
                                                        </Typography>
                                                    </Box>
                                                )}
                                                
                                                {/* Click indicator */}
                                                {clickCoordinates && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            left: `${clickCoordinates.x * 100}%`,
                                                            top: `${clickCoordinates.y * 100}%`,
                                                            transform: 'translate(-50%, -50%)',
                                                            width: '20px',
                                                            height: '20px',
                                                            borderRadius: '50%',
                                                            border: '2px solid #ff0000',
                                                            backgroundColor: 'rgba(255, 0, 0, 0.2)',
                                                            pointerEvents: 'none',
                                                            animation: 'fadeOut 2s forwards',
                                                            '@keyframes fadeOut': {
                                                                '0%': { opacity: 1 },
                                                                '100%': { opacity: 0 }
                                                            }
                                                        }}
                                                    />
                                                )}
                                                
                                            </Box>

                                            {/* Home Button - hide in fullscreen */}
                                            {!isFullscreenMode && (
                                            <Box sx={{
                                                position: 'absolute',
                                                bottom: '8px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '40px',
                                                height: '4px',
                                                backgroundColor: '#1a1a1a',
                                                borderRadius: '2px',
                                                zIndex: 1
                                            }} />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                
                                {/* Side controls - hide on mobile fullscreen, show as row on mobile non-fullscreen */}
                                {!isFullscreenMode && (
                                <Box sx={{ 
                                    display: 'flex',
                                    flexDirection: isMobile ? 'row' : 'column',
                                    gap: isMobile ? 1 : 2,
                                    justifyContent: 'center',
                                    alignItems: isMobile ? 'center' : 'flex-start',
                                    pr: isMobile ? 0 : 2,
                                    py: isMobile ? 1 : 0,
                                    flexWrap: isMobile ? 'wrap' : 'nowrap'
                                }}>
                                    {!isMobile && (
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Swipe Controls
                                    </Typography>
                                    )}
                                    <Tooltip title="Back to Kiosk Manager">
                                        <IconButton 
                                            onClick={() => handleSwipe('up')}
                                            sx={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                                                width: isMobile ? 44 : 40,
                                                height: isMobile ? 44 : 40
                                            }}
                                        >
                                            <HomeIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Swipe Down">
                                        <IconButton 
                                            onClick={() => handleSwipe('down')}
                                            sx={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                                                width: isMobile ? 44 : 40,
                                                height: isMobile ? 44 : 40
                                            }}
                                        >
                                            <ArrowDownwardIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Swipe Left">
                                        <IconButton 
                                            onClick={() => handleSwipe('left')}
                                            sx={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                                                width: isMobile ? 44 : 40,
                                                height: isMobile ? 44 : 40
                                            }}
                                        >
                                            <ArrowBackIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Swipe Right">
                                        <IconButton 
                                            onClick={() => handleSwipe('right')}
                                            sx={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                                                width: isMobile ? 44 : 40,
                                                height: isMobile ? 44 : 40
                                            }}
                                        >
                                            <ArrowForwardIcon />
                                        </IconButton>
                                    </Tooltip>

                                    {!isMobile && (
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                                        Scroll Controls
                                    </Typography>
                                    )}
                                    
                                    {/* Divider on mobile */}
                                    {isMobile && (
                                        <Box sx={{ width: 1, height: 24, borderLeft: '1px solid rgba(0,0,0,0.12)' }} />
                                    )}
                                    
                                    <Tooltip title="Scroll Up">
                                        <IconButton 
                                            onClick={() => handleDrag('down')}
                                            sx={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                                                width: isMobile ? 44 : 40,
                                                height: isMobile ? 44 : 40
                                            }}
                                        >
                                            <ArrowUpwardIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Scroll Down">
                                        <IconButton 
                                            onClick={() => handleDrag('up')}
                                            sx={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                                                width: isMobile ? 44 : 40,
                                                height: isMobile ? 44 : 40
                                            }}
                                        >
                                            <ArrowDownwardIcon />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Keyboard status - only show on desktop */}
                                    {!isMobile && (
                                    <Box 
                                        sx={{ 
                                            mt: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            backgroundColor: 'rgba(59, 130, 246, 0.15)',
                                            color: 'primary.main',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.75rem',
                                            fontWeight: 'medium'
                                        }}
                                    >
                                        ⌨️ Keyboard Active
                                    </Box>
                                    )}
                                    
                                    {/* Touch hint on mobile */}
                                    {isMobile && (
                                    <Box 
                                        sx={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            backgroundColor: 'rgba(59, 130, 246, 0.15)',
                                            color: 'primary.main',
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            fontSize: '0.7rem',
                                            fontWeight: 'medium'
                                        }}
                                    >
                                        <TouchAppIcon sx={{ fontSize: 14 }} /> Touch to interact
                                </Box>
                                    )}
                                </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            {/* Hide footer in remote access mode to maximize screen space */}
            {!deviceStatus.RemoteAccessMode && (
                <DialogActions sx={{ 
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1.5 : 2
                }}>
                    <Button 
                        onClick={onClose}
                        size={isMobile ? "medium" : "large"}
                    >
                        Close
                    </Button>
                </DialogActions>
            )}

            {/* Confirmation Dialogs */}
            <Dialog
                open={showRebootConfirmDialog}
                onClose={() => setShowRebootConfirmDialog(false)}
            >
                <DialogTitle>Confirm Reboot</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to reboot the device? This will restart the device and all running applications.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRebootConfirmDialog(false)}>Cancel</Button>
                    <Button onClick={handleRebootRequest} color="primary">
                        Confirm Reboot
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showResetPINConfirmDialog}
                onClose={() => setShowResetPINConfirmDialog(false)}
            >
                <DialogTitle>Confirm PIN Reset</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to reset the device PIN? This will restart the device and allow you to set a new PIN.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowResetPINConfirmDialog(false)}>Cancel</Button>
                    <Button onClick={handlePINResetRequest} color="primary">
                        Confirm Reset
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showUnlockConfirmDialog}
                onClose={() => setShowUnlockConfirmDialog(false)}
            >
                <DialogTitle>Confirm Unlock</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to unlock the device?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowUnlockConfirmDialog(false)}>Cancel</Button>
                    <Button onClick={handleUnlockRequest} color="primary">
                        Confirm Unlock
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Remote Access Confirmation Dialog */}
            <Dialog
                open={showRemoteAccessConfirmDialog}
                onClose={() => setShowRemoteAccessConfirmDialog(false)}
            >
                <DialogTitle>Confirm Remote Access</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to enable remote access mode? This will put the device into an intensive data mode.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRemoteAccessConfirmDialog(false)}>Cancel</Button>
                    <Button onClick={handleRemoteAccessRequest} color="primary">
                        Confirm Remote Access
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog
                open={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
            >
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <Typography>
                        {successMessage}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSuccessDialog(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Bug Report Dialog */}
            <Dialog
                open={showDeleteBugReportDialog}
                onClose={() => setShowDeleteBugReportDialog(false)}
            >
                <DialogTitle>Delete Bug Report</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the bug report? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteBugReportDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteBugReport} 
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Help Dialog */}
            <Dialog
                open={showHelpDialog}
                onClose={() => setShowHelpDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpOutlineIcon color="primary" />
                    Lockdown App Help
                </DialogTitle>
                <DialogContent>
                    <Typography component="div" sx={{ '& ul': { pl: 2, m: 0 }, '& li': { mb: 1 } }}>
                        <ul>
                            <li>The lockdown app checks regularly for instructions from the portal.</li>
                            <li>The device will only reboot if it's not already locally unlocked via the PIN.</li>
                            <li>If the device is not unlocking or rebooting, it may have lost internet connection, in which case you can restart (hard reset) the device manually, and login with the last set PIN.</li>
                            <li>If this doesn't resolve the issue, you can try updating the kiosk login credentials.</li>
                            <li>You can also unlock the device and access the device's settings app, where you can change the Wifi settings.</li>
                            <li>If you have lost the PIN altogether, the device will need reprovisioning: please contact support.</li>
                        </ul>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowHelpDialog(false)} color="primary">
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default LockdownAppSettingsDialog;
